import { Component, effect, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import dayjs from "dayjs";

import { GloControlComponent } from "../../../shared/components/glo-control/glo-control.component";
import { GloPaginationComponent } from "../../../shared/components/pagination/pagination.component";
import { BusinessCustomerDetailsComponent } from "../customer-details/customer-details.component";
import { BusinessCustomerAddComponent } from "../customer-add/customer-add.component";
import { Customer, CustomerStats } from "../../../shared/models/customer.model";
import { ApiService } from "../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { BusinessSalonContextService } from "../../../shared/services/api/business-salon-context.service";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "business-customer-relationship",
  standalone: true,
  imports: [
    LoaderComponent,
    GloControlComponent,
    GloPaginationComponent,
    CommonModule,
    FormsModule,
  ],
  templateUrl: "./customer-relationship.component.html",
  styleUrl: "./customer-relationship.component.scss",
})
export class BusinessCustomerRelationshipComponent implements OnInit {
  assetUrl = environment.assetBaseUrl;

  dialog: MatDialog = inject(MatDialog);
  readonly salonContext = inject(BusinessSalonContextService);

  isLoading = false;

  searchTerm = "";
  selectedSegment = "all";

  customersStats: CustomerStats = {} as CustomerStats;
  customers: any[] = [];
  filteredCustomers: any[] = [];
  viewCustomers: any[] = [];
  selectedCustomer: any | null = null;

  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  constructor(private apiService: ApiService) {
    /*
     * Reload the CRM whenever the global salon selection changes.
     *
     * BusinessComponent initializes the salon context. Waiting until the
     * salon list exists prevents an initial unscoped request.
     */
    effect(() => {
      const salons = this.salonContext.salons();
      this.salonContext.selectedSalonIds();

      if (!salons.length) {
        return;
      }

      this.loadCustomers();
    });
  }

  ngOnInit(): void {
    /*
     * Customer loading is handled by the salon-context effect.
     */
  }

  get totalLifetimeValue(): number {
    return this.customers.reduce(
      (sum, customer) => sum + this.toNumber(customer.lifetime_value),
      0,
    );
  }

  get atRiskCustomers(): number {
    return this.customers.filter((customer) => this.isAtRisk(customer)).length;
  }

  get activeCustomerPercent(): number {
    const total = this.toNumber(this.customersStats.total_customers);
    const active = this.toNumber(this.customersStats.repeat_customers);

    if (!total) return 0;

    return Math.round((active / total) * 100);
  }

  loadCustomers(): void {
    this.isLoading = true;

    const previouslySelectedCustomerId =
      this.selectedCustomer?.customer_id ?? null;

    const url = this.salonContext.withSalonScope(
      API_ENDPOINTS.owner.customers.list,
    );

    this.apiService.request(url, "GET").subscribe({
      next: (response: any) => {
        const customerResults =
          response?.customers?.results ??
          response?.results ??
          response?.customers ??
          [];

        this.customers = Array.isArray(customerResults) ? customerResults : [];

        this.customersStats = response?.stats ?? ({} as CustomerStats);

        /*
         * Reapply the current search / segment after changing salon scope.
         */
        this.applyFilters(false);

        if (previouslySelectedCustomerId) {
          this.selectedCustomer =
            this.customers.find(
              (customer) =>
                customer.customer_id === previouslySelectedCustomerId,
            ) ??
            this.viewCustomers[0] ??
            null;
        } else {
          this.selectedCustomer = this.viewCustomers[0] ?? null;
        }

        this.isLoading = false;
      },

      error: (error) => {
        console.error("Error loading customers:", error);

        /*
         * Clear stale data so a newly selected salon scope never continues
         * showing customers or KPIs from the previous scope.
         */
        this.customersStats = {} as CustomerStats;
        this.customers = [];
        this.filteredCustomers = [];
        this.viewCustomers = [];
        this.selectedCustomer = null;
        this.totalItems = 0;
        this.currentPage = 1;

        this.isLoading = false;
      },
    });
  }

  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm || "";
    this.applyFilters();
  }

  applyFilters(resetPage = true): void {
    const term = this.searchTerm.toLowerCase().trim();
    let results = [...this.customers];

    if (term) {
      results = results.filter((customer) => {
        return (
          customer.full_name?.toLowerCase().includes(term) ||
          customer.firstName?.toLowerCase().includes(term) ||
          customer.first_name?.toLowerCase().includes(term) ||
          customer.lastName?.toLowerCase().includes(term) ||
          customer.last_name?.toLowerCase().includes(term) ||
          customer.email?.toLowerCase().includes(term) ||
          customer.phone_number?.includes(term)
        );
      });
    }

    if (this.selectedSegment === "active") {
      results = results.filter(
        (customer) => this.toNumber(customer.total_visits) > 0,
      );
    }

    if (this.selectedSegment === "repeat") {
      results = results.filter(
        (customer) => this.toNumber(customer.total_visits) > 1,
      );
    }

    if (this.selectedSegment === "highValue") {
      results = results.filter(
        (customer) => this.toNumber(customer.lifetime_value) >= 250,
      );
    }

    if (this.selectedSegment === "atRisk") {
      results = results.filter((customer) => this.isAtRisk(customer));
    }

    if (this.selectedSegment === "birthdayMissing") {
      results = results.filter((customer) => !customer.birthday);
    }

    this.filteredCustomers = results;

    if (resetPage) {
      this.currentPage = 1;
    }

    this.totalItems = this.filteredCustomers.length;
    this.updatePaginatedCustomers();

    if (
      this.selectedCustomer &&
      !this.filteredCustomers.some(
        (customer) =>
          customer.customer_id === this.selectedCustomer?.customer_id,
      )
    ) {
      this.selectedCustomer = this.viewCustomers[0] ?? null;
    }
  }

  updatePaginatedCustomers(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;

    this.viewCustomers = this.filteredCustomers.slice(startIndex, endIndex);

    if (
      this.viewCustomers.length &&
      !this.viewCustomers.some(
        (customer) =>
          customer.customer_id === this.selectedCustomer?.customer_id,
      )
    ) {
      this.selectedCustomer = this.viewCustomers[0];
    }

    if (!this.viewCustomers.length) {
      this.selectedCustomer = null;
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePaginatedCustomers();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.updatePaginatedCustomers();
  }

  selectCustomer(customer: Customer): void {
    this.selectedCustomer = customer;
  }

  openCustomerDetails(customer: Customer): void {
    this.dialog.open(BusinessCustomerDetailsComponent, {
      panelClass: ["crm-detail-dialog"],
      data: {
        customer_id: customer.customer_id,
        selectedSalonIds: this.salonContext.selectedSalonIds(),
      },
      width: "760px",
      maxWidth: "100vw",
      height: "100vh",
      position: {
        right: "0",
        top: "0",
      },
    });
  }

  openCustomerAdd(): void {
    const dialogRef = this.dialog.open(BusinessCustomerAddComponent, {
      panelClass: ["crm-detail-dialog"],
      data: {
        customer_id: null,
        selectedSalonIds: this.salonContext.selectedSalonIds(),
      },
      width: "760px",
      maxWidth: "100vw",
      height: "100vh",
      position: {
        right: "0",
        top: "0",
      },
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh !== false) {
        this.loadCustomers();
      }
    });
  }

  openCustomerEdit(customerRecord: Customer): void {
    const dialogRef = this.dialog.open(BusinessCustomerAddComponent, {
      panelClass: ["crm-detail-dialog"],
      data: {
        ...customerRecord,
        selectedSalonIds: this.salonContext.selectedSalonIds(),
      },
      width: "760px",
      maxWidth: "100vw",
      height: "100vh",
      position: {
        right: "0",
        top: "0",
      },
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh !== false) {
        this.loadCustomers();
      }
    });
  }

  getInitials(customer: any): string {
    const name =
      customer.full_name ||
      this.getFullName(customer) ||
      customer.email ||
      "Customer";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0])
      .join("")
      .toUpperCase();
  }

  getFullName(customer: any): string {
    return [
      customer.firstName || customer.first_name,
      customer.lastName || customer.last_name,
    ]
      .filter(Boolean)
      .join(" ");
  }

  getFrequencyLabel(customer: any): string {
    const visits = this.toNumber(customer.total_visits);

    if (visits >= 12) return "Very frequent";
    if (visits >= 6) return "Frequent";
    if (visits >= 2) return "Occasional";
    if (visits === 1) return "New";

    return "No history";
  }

  getPreferredServices(customer: any): string[] {
    const values =
      customer.preferred_services ||
      customer.favorite_services ||
      customer.services ||
      customer.recent_services ||
      [];

    if (Array.isArray(values) && values.length) {
      return values.map((item: any) => item.name || item.service_name || item);
    }

    return ["No preferences yet"];
  }

  getFollowUpSuggestion(customer: any): string {
    if (!customer.last_visit) {
      return "No visit history yet. Consider sending a welcome offer after their first booking.";
    }

    if (this.isAtRisk(customer)) {
      return "This customer has not visited recently. Consider sending a rebooking reminder or loyalty offer.";
    }

    if (!customer.birthday) {
      return "Birthday is not added yet. Add it for birthday rewards and personalized promotions.";
    }

    if (this.toNumber(customer.total_visits) >= 6) {
      return "Strong repeat customer. Consider assigning VIP notes, preferred staff, or a loyalty reward.";
    }

    return "Customer relationship looks healthy. Keep notes updated after each visit.";
  }

  isAtRisk(customer: any): boolean {
    if (!customer.last_visit) return true;

    return dayjs().diff(dayjs(customer.last_visit), "day") >= 90;
  }

  toMoney(value: any): string {
    const amount = this.toNumber(value);

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }

  toNumber(value: any): number {
    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  formatDateString(dateString: string, format?: string): string {
    if (!dateString || !dayjs(dateString).isValid()) {
      return "";
    }

    return dayjs(dateString).format(format || "MMM D, YYYY");
  }
}
